import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Power, Rocket } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { computeSetupSteps } from '../../utils/setupReadiness';

/** Compact Publish / Disable controls for Basic Setup */
const WidgetPublishBar = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, settingsRes] = await Promise.all([
        api.getWidgetStatus(),
        api.getSettings().catch(() => null),
      ]);
      setStatus(statusRes?.status || null);
      setSettings(settingsRes || null);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const readiness = useMemo(() => computeSetupSteps(settings, status), [settings, status]);

  const publish = async () => {
    if (!readiness.requiredComplete) {
      const first = readiness.missingRequired[0];
      showToast(
        t('settings.widgetBar.finishFirst', {
          items: readiness.missingRequired.map((m) => t(m.labelKey)).join(', '),
        }),
        'warning',
      );
      if (first && onNavigate) onNavigate(first.route);
      return;
    }
    setActing(true);
    try {
      const data = await api.publishWidget();
      setStatus(data?.status || null);
      showToast(t('settings.widgetBar.published'), 'success');
    } catch (err) {
      showToast(err.message || t('settings.widgetBar.publishFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const disable = async () => {
    setActing(true);
    try {
      const data = await api.disableWidget();
      setStatus(data?.status || null);
      setDisableOpen(false);
      showToast(t('settings.widgetBar.disabled'), 'success');
    } catch (err) {
      showToast(err.message || t('settings.widgetBar.disableFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const live = Boolean(status?.live);

  return (
    <div className="border rounded p-3 mb-4 bg-light">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="fw-semibold">{t('settings.widgetBar.storefrontWidget')}</span>
            {loading ? (
              <span className="text-muted small">{t('settings.widgetBar.checking')}</span>
            ) : live ? (
              <Badge bg="success" pill>
                {t('settings.widgetBar.live')}
              </Badge>
            ) : (
              <Badge bg="secondary" pill>
                {t('settings.widgetBar.notPublished')}
              </Badge>
            )}
          </div>
          <p className="small text-muted mb-0">
            {t('settings.widgetBar.desc')}{' '}
            {/* {onNavigate && (
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => onNavigate(ROUTE.INSTALLATION)}>
                Installation details
              </button>
            )} */}
          </p>
        </div>
        <div className="d-flex gap-2">
          <OverlayTrigger
            placement="bottom"
            overlay={
              !live && !readiness.requiredComplete ? (
                <Tooltip id="publishbar-gate-tip">
                  {t('settings.widgetBar.finishFirst', {
                    items: readiness.missingRequired.map((m) => t(m.labelKey)).join(', '),
                  })}
                </Tooltip>
              ) : (
                <span />
              )
            }
          >
            <span className="d-inline-flex">
              <Button
                size="sm"
                variant="primary"
                className="d-flex align-items-center gap-1"
                onClick={publish}
                disabled={acting || live || loading || !readiness.requiredComplete}
              >
                <Rocket size={14} />
                {t('settings.widgetBar.publish')}
              </Button>
            </span>
          </OverlayTrigger>
          <Button
            size="sm"
            variant="outline-danger"
            className="d-flex align-items-center gap-1"
            onClick={() => setDisableOpen(true)}
            disabled={acting || !live || loading}
          >
            <Power size={14} />
            {t('settings.widgetBar.disable')}
          </Button>
        </div>
      </div>

      <Modal show={disableOpen} onHide={() => !acting && setDisableOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.widgetBar.disableModalTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('settings.widgetBar.disableModalBody')}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDisableOpen(false)} disabled={acting}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={disable} disabled={acting}>
            {acting ? t('settings.widgetBar.disabling') : t('settings.widgetBar.disable')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WidgetPublishBar;
